/**
 * Decides on boot whether a stored session is still good, from the `auth/ping` answer.
 *
 * Only a parsed API answer is definitive. Anything else says nothing about the session: an empty
 * or HTML body (dashboard-api re-wraps an upstream 502 as HTTP 200; a PHP fatal renders as a 200
 * error page) or a rejected call. Treating those as "logged out" bounced live users to the login
 * page on a transient backend hiccup.
 */

import { isJwtExpired } from './sessionExpiry';

type AuthPingVerdict = 'authenticated' | 'unauthenticated' | 'transient';

function classifyAuthPing(response: unknown): AuthPingVerdict {
  if (typeof response !== 'object' || response === null || !('Success' in response)) return 'transient';

  if (response.Success === true) return 'authenticated';
  if (response.Success === false) return 'unauthenticated';

  return 'transient';
}

const PING_ATTEMPTS = 2;

/**
 * Pings until it gets a definitive answer, at most twice. With none, falls back to the token's own
 * `exp`: an unexpired token keeps the session, and `installSessionExpiryDetection` still logs the
 * user out on the first request earthnode rejects. Never rejects, so the caller always settles
 * `isAuthenticated` — a thrown ping used to leave it `null` and the app blank.
 */
export async function verifyStoredSession(ping: () => Promise<unknown>, JWT: string): Promise<boolean> {
  for (let attempt = 0; attempt < PING_ATTEMPTS; attempt += 1) {
    // The hook has already logged a rejection, and a rejected ping is as inconclusive as a bad body.
    const verdict = classifyAuthPing(await ping().catch(() => undefined));

    if (verdict !== 'transient') return verdict === 'authenticated';
  }

  return !isJwtExpired(JWT);
}
